/**
 * Brand Setup's shared visual language (spec §2.2): dark canvas, a mono step
 * label, ONE display headline per screen, a ghost numeral behind it, a yellow
 * primary CTA. Everything is inline-styled from `tokens.css` variables — the
 * same idiom as every other screen in this app, and no new dependency.
 */
import type { CSSProperties, ReactNode } from "react";
import { RotateCcw } from "lucide-react";

export const MONO_LABEL: CSSProperties = {
  fontFamily: "var(--ql-font-mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".2em",
  textTransform: "uppercase",
  color: "var(--ql-fg-subtle)",
};

export const HEADLINE: CSSProperties = {
  fontSize: "clamp(30px, 4.6vw, 48px)",
  fontWeight: 800,
  lineHeight: 1.04,
  letterSpacing: "-0.025em",
  color: "var(--ql-fg)",
  margin: 0,
};

export const SUBCOPY: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.6,
  color: "var(--ql-fg-muted)",
  maxWidth: 580,
  margin: 0,
};

/**
 * The sentence Continue shows when something is missing (spec §1.3). It is
 * rendered TWICE by design — once at the control, by the step body, and once
 * under the button, by `StepFrame` — because the operator's eye is on
 * whichever of the two they just used.
 */
export const REASON: CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.5,
  fontWeight: 600,
  color: "var(--ql-danger)",
};

export const PANEL: CSSProperties = {
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.02)",
  borderRadius: 18,
  padding: 20,
};

export const BIG_INPUT: CSSProperties = {
  width: "100%",
  padding: "14px 0",
  border: "none",
  borderBottom: "2px solid rgba(255,255,255,.14)",
  background: "transparent",
  color: "var(--ql-fg)",
  fontSize: "clamp(22px, 3vw, 32px)",
  fontWeight: 700,
  letterSpacing: "-0.02em",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

export const SELECT: CSSProperties = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.10)",
  background: "rgba(17,24,39,.6)",
  color: "var(--ql-fg)",
  fontSize: 14,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

/** The enormous translucent step number that sits behind the headline. */
export function GhostNumeral({ n }: { n: number }): React.JSX.Element {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        top: "-0.22em",
        right: 0,
        fontSize: "clamp(150px, 24vw, 300px)",
        fontWeight: 900,
        lineHeight: 0.8,
        letterSpacing: "-0.06em",
        color: "rgba(255,255,255,.028)",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {String(n).padStart(2, "0")}
    </div>
  );
}

/** One of the two big lane cards on the Source screen. */
export function ChoiceCard(props: {
  title: string;
  copy: string;
  icon: ReactNode;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const { title, copy, icon, selected, disabled, onClick } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 10,
        textAlign: "left",
        padding: "22px 22px 24px",
        borderRadius: 20,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        opacity: disabled ? 0.45 : 1,
        border: selected ? "1.5px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.10)",
        background: selected ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
        transition: "all .2s var(--ql-ease)",
      }}
    >
      <span style={{ color: selected ? "var(--ql-yellow)" : "var(--ql-fg-subtle)" }}>{icon}</span>
      <span style={{ fontSize: 19, fontWeight: 700, color: "var(--ql-fg)", letterSpacing: "-0.01em" }}>{title}</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ql-fg-muted)" }}>{copy}</span>
    </button>
  );
}

const CONFIDENCE_COLOR: Record<string, string> = {
  high: "var(--ql-success)",
  medium: "var(--ql-warning)",
  low: "var(--ql-fg-subtle)",
};

/** Where a value came from, as a phrase. A bare "HIGH" told the user nothing —
 *  high WHAT? — and "measured" is our word, not theirs. */
const SOURCE_PHRASE: Record<string, string> = {
  measured: "from your reel",
  model: "read by the AI",
  default: "our default",
  adjusted: "from your reel, sized to fit",
};

/**
 * One thing the study read, what we did with it, and a way to undo it. Reset
 * never hides the row — it flips it to "back to the default", so the user can
 * always see what we found.
 */
export function ChipRow(props: {
  label: string;
  value: string;
  confidence: "high" | "medium" | "low";
  source: "measured" | "model" | "default" | "adjusted";
  applied: boolean;
  /** The user typed their own value here. Reset still has work to do, so the
   *  button stays live — otherwise a hand edit locked out the way back. */
  edited?: boolean;
  swatch?: string;
  /** Absent → the row is read-only (the Layout step, where the canvas itself
   *  is the way to change a number). */
  onReset?: () => void;
}): React.JSX.Element {
  const { label, value, confidence, source, applied, edited = false, swatch, onReset } = props;
  const canReset = applied || edited;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,.07)",
        background: applied ? "rgba(255,255,255,.025)" : "transparent",
        opacity: applied ? 1 : 0.55,
      }}
    >
      {swatch && (
        <span
          aria-hidden
          style={{ width: 18, height: 18, borderRadius: 6, background: swatch, border: "1px solid rgba(255,255,255,.18)", flexShrink: 0 }}
        />
      )}
      <span style={{ fontSize: 13, color: "var(--ql-fg)", fontWeight: 600, minWidth: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ql-fg-muted)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </span>
      <span
        title={
          source === "measured"
            ? "measured off the pixels of what you gave us"
            : source === "model"
              ? "read by the AI, so worth a glance"
              : source === "adjusted"
                ? "read off your reel, then sized so it fits the frame"
                : "what we start everyone on"
        }
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: CONFIDENCE_COLOR[confidence] ?? "var(--ql-fg-subtle)",
          whiteSpace: "nowrap",
        }}
      >
        {SOURCE_PHRASE[source] ?? source}
      </span>
      {onReset ? (
        <button
          type="button"
          onClick={onReset}
          disabled={!canReset}
          title="Put this back to the default"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "5px 10px",
            borderRadius: 9,
            border: "1px solid rgba(255,255,255,.10)",
            background: "transparent",
            color: canReset ? "var(--ql-fg-muted)" : "var(--ql-fg-faint)",
            fontSize: 11.5,
            fontFamily: "inherit",
            cursor: canReset ? "pointer" : "default",
            whiteSpace: "nowrap",
          }}
        >
          <RotateCcw size={11} />
          {canReset ? "Reset" : "Default"}
        </button>
      ) : (
        <span style={{ fontSize: 11.5, color: "var(--ql-fg-faint)", whiteSpace: "nowrap" }}>applied</span>
      )}
    </div>
  );
}

/**
 * "From your reel" — the mark a step wears when the study answered it, with
 * the one way back to our own value (spec §2.2, "From your reel").
 *
 * The wording is the CHIP'S, not ours: `source: "adjusted"` means the study's
 * own number would have overflowed the frame and we sized it to fit, and
 * saying so is the difference between a correction and a silent override
 * (spec §10.9).
 */
export function ReelTag({ label, onReset }: { label: string; onReset?: () => void }): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span
        style={{
          ...MONO_LABEL,
          color: "var(--ql-yellow)",
          padding: "4px 9px",
          borderRadius: 999,
          border: "1px solid rgba(255,213,0,.35)",
          background: "rgba(255,213,0,.06)",
        }}
      >
        {label}
      </span>
      {onReset && (
        <button
          type="button"
          onClick={onReset}
          style={{
            border: "none",
            background: "transparent",
            color: "var(--ql-fg-muted)",
            fontSize: 13,
            fontFamily: "inherit",
            textDecoration: "underline",
            cursor: "pointer",
            padding: 0,
          }}
        >
          Use ours instead
        </button>
      )}
    </div>
  );
}

/**
 * The two buttons every screen outside `StepFrame` uses — Review's Create /
 * Adjust / Back, Done's three CTAs, Welcome's Start. Declared ONCE so the
 * wizard has one primary, not five hand-rolled gradients that drift apart.
 */
export function primaryButton(busy = false): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "15px 28px",
    borderRadius: 16,
    border: "none",
    fontWeight: 800,
    fontSize: 15,
    fontFamily: "inherit",
    color: busy ? "var(--ql-fg-subtle)" : "#000",
    background: busy ? "rgba(255,255,255,.04)" : "linear-gradient(135deg, var(--ql-yellow), #FFA500)",
    boxShadow: busy ? "none" : "0 4px 16px rgba(255,213,0,.30)",
    cursor: busy ? "wait" : "pointer",
  };
}

export function secondaryButton(busy = false): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "15px 24px",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,.12)",
    background: "transparent",
    color: "var(--ql-fg-muted)",
    fontWeight: 700,
    fontSize: 14,
    fontFamily: "inherit",
    cursor: busy ? "wait" : "pointer",
  };
}

/** A labelled block — the only wrapper the step bodies need. */
export function Block({ label, hint, children }: { label: string; hint?: string; children: ReactNode }): React.JSX.Element {
  return (
    <div>
      <div style={{ ...MONO_LABEL, marginBottom: 10 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 12.5, color: "var(--ql-fg-subtle)", lineHeight: 1.55, marginTop: 8 }}>{hint}</div>}
    </div>
  );
}
